'use client';
import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface GoalEntry {
  goalType: string;
  title: string;
  description: string;
  targetValue: string;
  targetUnit: string;
  startValue: string;
  targetDate: string;
}

interface GoalOnboardingProps {
  profileName: string;
  onComplete: (data: {
    profileName: string;
    currentWeight: number;
    targetWeight: number;
    heightCm: number;
    age: number;
    gender: string;
    activityLevel: string;
    targetCalories: number;
    targetProtein: number;
    targetCarbs: number;
    targetFat: number;
    missionStartDate: string;
    goals: GoalEntry[];
  }) => void;
  isLoading?: boolean;
}

const GOAL_TEMPLATES = [
  { type: 'weight_loss', label: 'Lose Weight', unit: 'kg', icon: 'ArrowTrendingDownIcon', placeholder: 'e.g. 85' },
  { type: 'body_composition', label: 'Body Fat %', unit: '%', icon: 'FireIcon', placeholder: 'e.g. 18' },
  { type: 'strength', label: 'Strength Goal', unit: 'kg', icon: 'BoltIcon', placeholder: 'e.g. 100' },
  { type: 'habit', label: 'Daily Habit', unit: 'days', icon: 'CheckCircleIcon', placeholder: 'e.g. 90' },
  { type: 'custom', label: 'Custom Goal', unit: '', icon: 'StarIcon', placeholder: 'Target value' },
];

const ACTIVITY_LEVELS = [
  { value: 'sedentary', label: 'Sedentary', desc: 'Little or no exercise' },
  { value: 'light', label: 'Light', desc: '1–3 days/week' },
  { value: 'moderate', label: 'Moderate', desc: '3–5 days/week' },
  { value: 'active', label: 'Active', desc: '6–7 days/week' },
  { value: 'very_active', label: 'Very Active', desc: 'Twice daily / physical job' },
];

export default function GoalOnboarding({ profileName, onComplete, isLoading }: GoalOnboardingProps) {
  const [step, setStep] = useState(1);
  const [currentWeight, setCurrentWeight] = useState('');
  const [targetWeight, setTargetWeight] = useState('');
  const [heightCm, setHeightCm] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('male');
  const [activityLevel, setActivityLevel] = useState('moderate');
  const [targetCalories, setTargetCalories] = useState('');
  const [targetProtein, setTargetProtein] = useState('');
  const [targetCarbs, setTargetCarbs] = useState('');
  const [targetFat, setTargetFat] = useState('');
  const [goals, setGoals] = useState<GoalEntry[]>([
    { goalType: 'weight_loss', title: 'Reach Target Weight', description: '', targetValue: '', targetUnit: 'kg', startValue: '', targetDate: '' },
  ]);

  const today = new Date().toISOString().split('T')[0];

  function addGoal() {
    setGoals((prev) => [
      ...prev,
      { goalType: 'custom', title: '', description: '', targetValue: '', targetUnit: '', startValue: '', targetDate: '' },
    ]);
  }

  function removeGoal(index: number) {
    setGoals((prev) => prev.filter((_, i) => i !== index));
  }

  function updateGoal(index: number, field: keyof GoalEntry, value: string) {
    setGoals((prev) => prev.map((g, i) => (i === index ? { ...g, [field]: value } : g)));
  }

  function handleSubmit() {
    onComplete({
      profileName,
      currentWeight: parseFloat(currentWeight) || 0,
      targetWeight: parseFloat(targetWeight) || 0,
      heightCm: parseFloat(heightCm) || 0,
      age: parseInt(age) || 0,
      gender,
      activityLevel,
      targetCalories: parseInt(targetCalories) || 2000,
      targetProtein: parseInt(targetProtein) || 150,
      targetCarbs: parseInt(targetCarbs) || 200,
      targetFat: parseInt(targetFat) || 65,
      missionStartDate: today,
      goals: goals.filter((g) => g.title.trim()),
    });
  }

  const canProceedStep1 = currentWeight && targetWeight && heightCm && age;
  const canProceedStep2 = targetCalories && targetProtein;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-card border border-border rounded-2xl overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/20 to-primary/5 border-b border-border px-6 py-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-primary/20 border-2 border-primary/50 flex items-center justify-center">
              <span className="text-xl font-black text-primary">{profileName.charAt(0).toUpperCase()}</span>
            </div>
            <div>
              <div className="text-xs text-primary font-bold tracking-widest uppercase">First Mission Brief</div>
              <h2 className="text-xl font-black tracking-wider text-foreground">{profileName.toUpperCase()}</h2>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Day 1 starts today — {new Date().toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
          {/* Step indicator */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all ${s <= step ? 'bg-primary' : 'bg-border'}`}
              />
            ))}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            Step {step} of 3 — {step === 1 ? 'Body Stats' : step === 2 ? 'Macro Targets' : 'Forged Goals'}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto scrollbar-forge">
          {/* STEP 1: Body Stats */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-forge-label mb-4">Your Body Stats</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Current Weight (kg)</label>
                  <input
                    type="number"
                    value={currentWeight}
                    onChange={(e) => setCurrentWeight(e.target.value)}
                    placeholder="e.g. 99"
                    inputMode="decimal"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Target Weight (kg)</label>
                  <input
                    type="number"
                    value={targetWeight}
                    onChange={(e) => setTargetWeight(e.target.value)}
                    placeholder="e.g. 85"
                    inputMode="decimal"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Height (cm)</label>
                  <input
                    type="number"
                    value={heightCm}
                    onChange={(e) => setHeightCm(e.target.value)}
                    placeholder="e.g. 178"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Age</label>
                  <input
                    type="number"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    placeholder="e.g. 32"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-2">Gender</label>
                <div className="grid grid-cols-2 gap-2">
                  {['male', 'female'].map((g) => (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setGender(g)}
                      className={`py-2.5 rounded-md text-sm font-bold capitalize transition-all ${
                        gender === g ? 'bg-primary text-primary-foreground' : 'bg-input border border-border text-muted-foreground hover:border-primary/40'
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-2">Activity Level</label>
                <div className="space-y-1.5">
                  {ACTIVITY_LEVELS.map((a) => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => setActivityLevel(a.value)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-md text-sm transition-all ${
                        activityLevel === a.value
                          ? 'bg-primary/15 border border-primary/50 text-foreground'
                          : 'bg-input border border-border text-muted-foreground hover:border-primary/30'
                      }`}
                    >
                      <span className="font-bold">{a.label}</span>
                      <span className="text-xs opacity-70">{a.desc}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Macro Targets */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-forge-label mb-1">Daily Macro Targets</div>
              <p className="text-xs text-muted-foreground mb-4">Set your daily nutrition targets. These will be your compliance benchmarks.</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-foreground">Daily Calories</span> (kcal)
                  </label>
                  <input
                    type="number"
                    value={targetCalories}
                    onChange={(e) => setTargetCalories(e.target.value)}
                    placeholder="e.g. 2200"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-primary">Protein</span> (g)
                  </label>
                  <input
                    type="number"
                    value={targetProtein}
                    onChange={(e) => setTargetProtein(e.target.value)}
                    placeholder="e.g. 180"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-accent">Carbs</span> (g)
                  </label>
                  <input
                    type="number"
                    value={targetCarbs}
                    onChange={(e) => setTargetCarbs(e.target.value)}
                    placeholder="e.g. 200"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    <span className="text-amber-400">Fat</span> (g)
                  </label>
                  <input
                    type="number"
                    value={targetFat}
                    onChange={(e) => setTargetFat(e.target.value)}
                    placeholder="e.g. 65"
                    inputMode="numeric"
                    className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div className="bg-card-elevated rounded-lg p-3 border border-border/50">
                <div className="text-xs text-muted-foreground">
                  <span className="text-primary font-bold">TIP:</span> For fat loss, aim for 1.8–2.2g protein per kg of bodyweight. Calories should create a moderate deficit (300–500 kcal below maintenance).
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Goals */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="text-forge-label mb-1">Forged Goals</div>
              <p className="text-xs text-muted-foreground mb-4">Define what you are fighting for. Each goal achieved earns a badge.</p>
              {goals.map((goal, index) => (
                <div key={index} className="bg-card-elevated rounded-xl p-4 border border-border/50 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-bold text-primary uppercase tracking-wider">Goal {index + 1}</div>
                    {goals.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeGoal(index)}
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Goal Type</label>
                    <select
                      value={goal.goalType}
                      onChange={(e) => {
                        const tmpl = GOAL_TEMPLATES.find((t) => t.type === e.target.value);
                        updateGoal(index, 'goalType', e.target.value);
                        if (tmpl) updateGoal(index, 'targetUnit', tmpl.unit);
                      }}
                      className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                    >
                      {GOAL_TEMPLATES.map((t) => (
                        <option key={t.type} value={t.type}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Goal Title</label>
                    <input
                      type="text"
                      value={goal.title}
                      onChange={(e) => updateGoal(index, 'title', e.target.value)}
                      placeholder="e.g. Reach 85kg"
                      className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Start Value</label>
                      <input
                        type="number"
                        value={goal.startValue}
                        onChange={(e) => updateGoal(index, 'startValue', e.target.value)}
                        placeholder={currentWeight || 'e.g. 99'}
                        inputMode="decimal"
                        className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Target Value</label>
                      <input
                        type="number"
                        value={goal.targetValue}
                        onChange={(e) => updateGoal(index, 'targetValue', e.target.value)}
                        placeholder={targetWeight || 'e.g. 85'}
                        inputMode="decimal"
                        className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary/50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">Target Date (optional)</label>
                    <input
                      type="date"
                      value={goal.targetDate}
                      onChange={(e) => updateGoal(index, 'targetDate', e.target.value)}
                      min={today}
                      className="w-full bg-input border border-border rounded-md px-3 py-2.5 text-sm text-foreground focus:outline-none focus:border-primary/50"
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addGoal}
                className="w-full py-2.5 rounded-xl border border-dashed border-border text-muted-foreground hover:border-primary/40 hover:text-primary transition-all text-xs font-bold tracking-wider flex items-center justify-center gap-2"
              >
                <Icon name="PlusIcon" size={14} />
                ADD ANOTHER GOAL
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex gap-3">
          {step > 1 && (
            <button
              type="button"
              onClick={() => setStep((s) => s - 1)}
              className="btn-forge-ghost px-4 py-2.5 rounded-md text-sm font-bold"
            >
              BACK
            </button>
          )}
          {step < 3 ? (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              disabled={step === 1 ? !canProceedStep1 : !canProceedStep2}
              className="flex-1 btn-forge-primary py-2.5 rounded-md text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              NEXT →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isLoading || goals.filter((g) => g.title.trim()).length === 0}
              className="flex-1 btn-forge-primary py-2.5 rounded-md text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  FORGING...
                </>
              ) : (
                'BEGIN THE FORGE ⚡'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
